import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Paper,
  InputAdornment,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  CloudUpload as CloudUploadIcon,
  CalendarToday as CalendarTodayIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  uploadConvertsScannerFile,
  refreshConvertsScannerHistoricalFields,
  getConvertsScannerUniqueDates,
  checkConvertsScannerDateExists,
  deleteConvertsScannerByDate,
} from "../../../services/Axios";

const DataManagementTab = () => {
  const theme = useTheme();

  // State
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshAllRecords, setRefreshAllRecords] = useState(true);
  const [refreshPasswordDialogOpen, setRefreshPasswordDialogOpen] = useState(false);
  const [refreshPassword, setRefreshPassword] = useState("");
  const [uniqueDates, setUniqueDates] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [dateExists, setDateExists] = useState(false);

  // Fetch unique dates from backend
  const fetchUniqueDates = useCallback(async () => {
    try {
      setIsLoadingDates(true);
      const response = await getConvertsScannerUniqueDates();
      if (response?.dates) {
        setUniqueDates(response.dates);
      }
    } catch (err) {
      // Error handled silently
    } finally {
      setIsLoadingDates(false);
    }
  }, []);

  // Fetch unique dates on mount
  useEffect(() => {
    fetchUniqueDates();
  }, [fetchUniqueDates]);

  // Fetch unique dates after successful upload or refresh
  useEffect(() => {
    if (error?.includes("successfully") || error?.includes("refreshed")) {
      setTimeout(() => {
        fetchUniqueDates();
      }, 1000);
    }
  }, [error, fetchUniqueDates]);

  // File upload handler
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (fileExtension === "csv" || fileExtension === "xlsx" || fileExtension === "xls") {
        setSelectedFile(file);
        setError(null);
      } else {
        setError("Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
        setSelectedFile(null);
      }
    }
  };

  // Date change handler
  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  // Submit handler - checks if date exists, then opens override dialog or password dialog
  const handleSubmit = async () => {
    if (!selectedFile) {
      setError("Please select a file to upload");
      return;
    }
    if (!selectedDate) {
      setError("Please select a date");
      return;
    }
    setError(null);

    try {
      // Check if date already exists
      const dateCheck = await checkConvertsScannerDateExists(selectedDate);

      if (dateCheck?.exists) {
        // Date exists, show override confirmation dialog
        setDateExists(true);
        setOverrideDialogOpen(true);
      } else {
        // Date doesn't exist, proceed directly to password dialog
        setDateExists(false);
        setPasswordDialogOpen(true);
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.message ||
        "Error checking date. Please try again.";
      setError(errorMessage);
    }
  };

  // Override confirmation handler
  const handleOverrideConfirm = async () => {
    setOverrideDialogOpen(false);

    try {
      setIsSubmitting(true);
      setError(null);

      // Delete existing records for this date (password is required by backend)
      await deleteConvertsScannerByDate(selectedDate, "Glazer123");

      // Refresh the dates table to reflect the deletion
      await fetchUniqueDates();

      // Reset submitting state before opening password dialog
      setIsSubmitting(false);

      // After deletion, proceed to password dialog for upload
      setPasswordDialogOpen(true);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.message ||
        "Error deleting existing data. Please try again.";
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  // Override cancel handler
  const handleOverrideCancel = () => {
    setOverrideDialogOpen(false);
    setDateExists(false);
  };

  // Password confirmation handler
  const handlePasswordConfirm = async () => {
    if (password !== "Glazer123") {
      setError("Invalid password. Please enter the correct password.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Upload file to backend
      const response = await uploadConvertsScannerFile(selectedFile, selectedDate);

      // Close dialog and reset
      setPasswordDialogOpen(false);
      setPassword("");
      setSelectedFile(null);
      setSelectedDate(new Date().toISOString().split("T")[0]);
      setDateExists(false);

      // Reset the file input element so user can select a new file
      const fileInput = document.getElementById("file-upload");
      if (fileInput) {
        fileInput.value = "";
      }

      // Refresh the dates table to show the newly uploaded date
      await fetchUniqueDates();

      // Show success message with details
      const successMessage =
        response?.message ||
        `File uploaded successfully! Created: ${response?.records_created || 0}, Updated: ${response?.records_updated || 0}`;
      setError(successMessage);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.message ||
        "Error uploading file. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close password dialog
  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPassword("");
  };

  // Clear selected file
  const handleClearFile = () => {
    setSelectedFile(null);
    setError(null);
    // Reset the file input
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Refresh historical fields handler - opens password dialog if refreshAllRecords is checked
  const handleRefreshHistoricalFields = () => {
    if (refreshAllRecords) {
      // Require password for refreshing all records
      setRefreshPasswordDialogOpen(true);
    } else {
      // No password needed for single date refresh
      executeRefresh();
    }
  };

  // Execute the actual refresh
  const executeRefresh = async () => {
    try {
      setIsRefreshing(true);
      setError(null);

      // Refresh historical fields for the selected date (or all records if refreshAllRecords is true)
      // Pass null to refresh all records, or selectedDate to refresh only that date
      const dateToRefresh = refreshAllRecords ? null : selectedDate || null;
      const response = await refreshConvertsScannerHistoricalFields(dateToRefresh);

      // Show success message with details
      const dateText = dateToRefresh ? `for ${dateToRefresh}` : "for all records";
      const successMessage =
        response?.message ||
        `Historical fields refreshed successfully ${dateText}! Updated: ${response?.records_updated || 0}`;
      setError(successMessage);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.message ||
        "Error refreshing historical fields. Please try again.";
      setError(errorMessage);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Password confirmation handler for refresh
  const handleRefreshPasswordConfirm = async () => {
    if (refreshPassword !== "Converts123") {
      setError("Invalid password. Please enter the correct password.");
      return;
    }

    // Close dialog and reset password
    setRefreshPasswordDialogOpen(false);
    setRefreshPassword("");

    // Execute the refresh
    await executeRefresh();
  };

  // Close refresh password dialog
  const handleRefreshPasswordDialogClose = () => {
    setRefreshPasswordDialogOpen(false);
    setRefreshPassword("");
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: 4,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
        boxShadow: theme.shadows[1],
      }}
    >
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Data Management
      </Typography>

      {/* Error/Success Alert */}
      {error && (
        <Alert
          severity={error.includes("successfully") ? "success" : "error"}
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 2 }}>
        {/* Compact horizontal layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          {/* Date Picker - First */}
          <TextField
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            size="small"
            sx={{ minWidth: { xs: "100%", sm: 180 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              max: new Date().toISOString().split("T")[0],
            }}
          />

          {/* Browse Button - Second */}
          <Box>
            <input
              accept=".csv,.xlsx,.xls"
              style={{ display: "none" }}
              id="file-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 150 }, width: { sm: "auto" } }}
              >
                Browse Files
              </Button>
            </label>
          </Box>

          {/* Submit Button */}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedFile || !selectedDate || isSubmitting}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 120 },
              width: { sm: "auto" },
              backgroundColor:
                selectedFile && selectedDate
                  ? "#4caf50"
                  : theme.palette.action.disabledBackground,
              color:
                selectedFile && selectedDate
                  ? "#ffffff"
                  : theme.palette.action.disabled,
              "&:hover": {
                backgroundColor:
                  selectedFile && selectedDate ? "#45a049" : undefined,
              },
              "&:disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            Submit
          </Button>

          {/* Refresh All Records Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={refreshAllRecords}
                onChange={(e) => setRefreshAllRecords(e.target.checked)}
                size="small"
              />
            }
            label="Refresh All Records"
            sx={{
              minWidth: { xs: "100%", sm: "auto" },
              whiteSpace: "nowrap",
            }}
          />

          {/* Refresh Historical Fields Button */}
          <Button
            variant="outlined"
            onClick={handleRefreshHistoricalFields}
            disabled={isRefreshing}
            size="small"
            startIcon={<RefreshIcon />}
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              width: { sm: "auto" },
            }}
          >
            {isRefreshing
              ? "Refreshing..."
              : refreshAllRecords
                ? "Refresh All Historical Fields"
                : `Refresh Historical Fields (${selectedDate})`}
          </Button>
        </Box>

        {/* File Name Display */}
        {selectedFile && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              backgroundColor: theme.palette.action.hover,
              borderRadius: 1,
              width: "fit-content",
            }}
          >
            <FileIcon fontSize="small" color="primary" />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.primary,
                maxWidth: "400px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedFile.name}
            </Typography>
            <Chip
              label={selectedFile.name.split(".").pop().toUpperCase()}
              size="small"
              color="primary"
              variant="outlined"
            />
            <IconButton
              size="small"
              onClick={handleClearFile}
              sx={{
                ml: 0.5,
                color: theme.palette.text.secondary,
                "&:hover": {
                  color: theme.palette.error.main,
                  backgroundColor: theme.palette.error.light + "20",
                },
              }}
              aria-label="Remove file"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>

      {/* Unique Dates Table */}
      <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Uploaded Quote Dates
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, mb: 2 }}
        >
          View all quote dates that have been uploaded to the database. This helps
          you avoid uploading duplicate dates.
        </Typography>

        {isLoadingDates ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : uniqueDates.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              p: 2,
              textAlign: "center",
            }}
          >
            No dates found in the database.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Quote Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>First Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uniqueDates.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarTodayIcon fontSize="small" color="primary" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.quote_date}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.created_at
                          ? new Date(item.created_at).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Override Confirmation Dialog */}
      <Dialog
        open={overrideDialogOpen}
        onClose={handleOverrideCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Date Already Exists</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            The date <strong>{selectedDate}</strong> already exists in the database.
          </Typography>
          <Typography sx={{ mb: 2, color: theme.palette.warning.main }}>
            Would you like to override? This will delete all existing data for this
            date and replace it with the new upload.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOverrideCancel} color="inherit">
            No
          </Button>
          <Button
            onClick={handleOverrideConfirm}
            variant="contained"
            color="warning"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Deleting..." : "Yes, Override"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter Password</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please enter the password to confirm file upload.
          </Typography>
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            placeholder="Enter password"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handlePasswordConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordDialogClose}>Cancel</Button>
          <Button
            onClick={handlePasswordConfirm}
            variant="contained"
            disabled={!password || isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refresh Password Dialog */}
      <Dialog
        open={refreshPasswordDialogOpen}
        onClose={handleRefreshPasswordDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter Password</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please enter the password to confirm refreshing all historical fields.
          </Typography>
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={refreshPassword}
            onChange={(e) => setRefreshPassword(e.target.value)}
            margin="normal"
            placeholder="Enter password"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleRefreshPasswordConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRefreshPasswordDialogClose}>Cancel</Button>
          <Button
            onClick={handleRefreshPasswordConfirm}
            variant="contained"
            disabled={!refreshPassword || isRefreshing}
          >
            {isRefreshing ? "Refreshing..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataManagementTab;
